/**
 * @format
 * @module lib/commercial/permutive
 * @description Permutive is a Data Management Platform (DMP) that replaced Krux.
 * Basically, they help us collect, analyze and activate customer data in real time.
 * @see https://developer.permutive.com/page/the-permutive-javascript-sdk#identifying-users
 * @see https://developer.permutive.com/docs/web
 */
import tgamConfig from "config/tgam";
import { getProp } from "utils/utils";
import { getAkamaiCookie, selectAkamaiLocation } from "lib/identity/akamai";
import {
  isLoggedIn,
  getCampaignCodeFromServiceString,
  getUserSophiId,
  roles
} from "lib/identity/api";
import { gptSetTarget, gptGetTarget } from "lib/commercial/dfp-ads/gpt-ads";
import logger from "utils/logger";

const debugTag = "[DFP-ADS] Permutive";

let permutiveEnabled = getProp(tgamConfig, "env.permutiveEnabled", false);

// List includes the countries/regions that should be excluded and their iso-3166 code,
// which is what Akamai uses. Key is "country code" and value is optional "region code".
const excludeList = {
  AT: [], // Austria
  BE: [], // Belgium
  BG: [], // Bulgaria
  HR: [], // Croatia
  CY: [], // Cyprus
  CZ: [], // Czechia
  DK: [], // Denmark
  EE: [], // Estonia
  FI: [], // Finland
  FR: [], // France
  DE: [], // Germany
  GR: [], // Greece
  HU: [], // Hungary
  IE: [], // Ireland
  IT: [], // Italy
  LV: [], // Latvia
  LT: [], // Lithuania
  LU: [], // Luxembourg
  MT: [], // Malta
  NL: [], // Netherlands
  PL: [], // Poland
  PT: [], // Portugal
  RO: [], // Romania
  SK: [], // Slovakia
  SI: [], // Slovenia
  ES: [], // Spain
  SE: [], // Sweden
  GB: [], // Great Britain, includes: North Ireland, Scotland, Wales
  US: ["CA", "VA"] // United States - [California, Virginia]
};

/**
 * Get an Array of Strings from a comma-delimited String by a nested object path.
 * If String length is zero, then return an empty Array.
 * @param {Object} obj - The object to query.
 * @param {String} path - The path of the property to get.
 * @param {String} [def=""] - The value returned for undefined resolved values.
 * @returns {Array<String>}
 */
function getArray(obj, path, def = "") {
  const str = getProp(obj, path, def);
  return str.length ? str.split(",") : [];
}

/**
 * Get an Number/Float value by a nested object path.
 * @param {Object} obj - The object to query.
 * @param {String} path - The path of the property to get.
 * @param {String} [def=""] - The value returned for undefined resolved values.
 * @returns {Number}
 */
function getFloat(obj, path, def = 0) {
  return Number(getProp(obj, path, def));
}

/**
 * Get an Integer value by a nested object path.
 * @param {Object} obj - The object to query.
 * @param {String} path - The path of the property to get.
 * @param {String} [def=""] - The value returned for undefined resolved values.
 * @returns {Number}
 */
function getInt(obj, path, def = 0) {
  return parseInteger(getProp(obj, path, def));
}

/**
 * Parse a value to get a base 10 Integer result.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/parseInt
 * @param {*} value
 * @returns {Number}
 */
function parseInteger(value) {
  return parseInt(value, 10);
}

/**
 * Associate all events tracked afterwards with an id and tag related to the user.
 * @param {Object} c - collection of user ids
 * @param {String} c.hash - Decrypted hash id - Don't share externally with third parties.
 * @param {String} c.hashExternal - Encrypted hash id - Safe to share externally.
 * @param {String} c.sophi3 - Sophi 3 user id
 * @param {String} c.sophi - Sophi 2 user id
 */
export default function permutiveInit({ hash, hashExternal, sophi3, sophi }) {
  if (!permutiveEnabled || typeof window.permutive === "undefined") {
    return;
  }

  // ARC-7293: Check if user should be excluded due to their geographic location
  const [country, region] = selectAkamaiLocation(getAkamaiCookie());
  const excluded = isUserExcluded(excludeList, country, region);
  logger.log(`${debugTag} user:`, { excluded, country, region });
  if (excluded) {
    permutiveEnabled = false;
    return;
  }

  // Order ids by priority from most important (0) to least (3)
  const identity = [];
  const userLoggedIn = isLoggedIn();

  if (userLoggedIn) {
    if (hashExternal) {
      identity.push({
        id: hashExternal,
        tag: "hashed-user-id",
        priority: 0
      });
    }
    if (hash) {
      identity.push({
        id: hash, // TODO: Eventually remove after minimum 90 days.
        tag: "internal",
        priority: 1
      });
    }
  }

  // For all user types
  if (sophi3) {
    identity.push({
      id: sophi3,
      tag: "duid3",
      priority: 2
    });
  }
  if (sophi) {
    identity.push({
      id: sophi,
      tag: "duid2",
      priority: 3
    });
  }

  // The page view event
  const event = { page: permutivePageEvent(tgamConfig, userLoggedIn ? hashExternal : "") };

  // The SDK is internally initialised, but no events are yet tracked.
  window.permutive.ready(() => {
    // Identify the user - before page view event.
    logger.log(`${debugTag} identity:`, identity);
    if (identity.length) {
      window.permutive.identify(identity);
    }
    // Trigger page view event - after identifying
    logger.log(`${debugTag} pageview:`, event);
    window.permutive.addon("web", event);
  }, "initialised");
}

/**
 * Map the TGAM config to a Permutive page event.
 * @param {Object} [config={}] - TGAM config
 * @param {String} [hashExternal] - encrypted user hash id
 * @returns {Object}
 */
function permutivePageEvent(config = {}, hashExternal = "") {
  const { datalayer = {}, meta = {}, adops = {} } = config;
  const { article = {}, identity = {}, page = {} } = datalayer;

  const isVideoPage = getProp(meta, "isVideoPage", false);

  return {
    article: {
      advcontent: getProp(article, "advContent", false), // Boolean
      byline: getProp(article, "byline", ""), // String
      contentid: getProp(article, "contentId", ""), // String
      creditline: getProp(article, "creditline", ""), // String
      isinprint: getProp(article, "isInPrint", ""), // String
      keywords: getArray(article, "keywords"), // String "List,of,Strings" to Array<String>
      pagesubtype: getProp(article, "pagesubtype", ""), // String
      paywallStatus: getProp(article, "paywallStatus", ""), // String
      seokeywords: getArray(article, "seoKeywords"), // String "List,of,Strings" to Array<String>
      slug: getProp(article, "slug", ""), // String
      title: getProp(article, "title", "") // String
    },
    financial: {
      symbol: getProp(meta, "financialSymbol", "") // String
    },
    identity: {
      birthyear: getProp(identity, "birthYear", ""), // String - Anonymized as "1" ?
      country: getProp(identity, "country", ""), // String
      gender: getProp(identity, "gender", ""), // String - Anonymized as "1" ?
      guid: hashExternal || "", // String - Share the encrypted user hash id
      incomerange: getProp(identity, "incomeRange", ""), // String - Anonymized as "1" ?
      industry: getProp(identity, "industry", ""), // String - Anonymized as "1" ?
      newsletter: getArray(identity, "newsletter").map(parseInteger), // String "List,of,Integers" to Array<Integer>
      position: getProp(identity, "position", ""), // String - Anonymized as "1" ?
      postal: getProp(identity, "postal", ""), // String
      prizm: getInt(identity, "prizm"), // Integer
      propensity: getInt(identity, "propensity.PREMIUM.churn"), // Integer
      rfv: {
        premium: {
          f: getInt(identity, "rfv.PREMIUM.f"), // Integer
          r: getInt(identity, "rfv.PREMIUM.r"), // Integer
          score: getFloat(identity, "rfv.PREMIUM.score"), // Float
          v: getFloat(identity, "rfv.PREMIUM.v") // Float
        }
      },
      role: {
        id: getProp(identity, "role.id", roles.anonymous.id), // Integer
        name: getProp(identity, "role.name", "") // String
      },
      services: {
        campaigncode: getCampaignCodeFromServiceString() // String - Final part of `ssvcs`
      },
      ssvcs: getArray(identity, "ssvcs"), // String "List,of,Strings" to Array<String>
      tenure: {
        canpro: { tenure: getInt(identity, "tenure.CANPRO.tenure") }, // Integer
        nytprint: { tenure: getInt(identity, "tenure.NYTPRINT.tenure") }, // Integer
        premium: { tenure: getInt(identity, "tenure.PREMIUM.tenure") } // Integer
      },
      userattributes: {
        audio_language: getProp(identity, "userAttributes.AUDIO_LANGUAGE", ""), // String
        audio_speed: getFloat(identity, "userAttributes.AUDIO_SPEED", 1) // String to Float
      },
      visid: getUserSophiId(), // String - Same as: tgam.identity.visid
      watchlist: getProp(identity, "watchlist", "n") // String - "n" or "y"
    },
    keywords: getArray(meta, "keywords"), // String "List,of,Strings" to Array<String>
    pagegroup: {
      advcontent: getProp(page, "advContent", false), // Boolean
      amp: getProp(page, "amp", false), // Boolean
      hierarchy: getProp(page, "hierarchy", ""), // String
      subsections: getProp(page, "subsections", []), // Array ["List", "of", "Strings"]
      topics: getProp(page, "topics", []) // Array ["List", "of", "Strings"]
    },
    pagename: getProp(meta, "pagename", ""), // String:of:section:path
    pagetype: getProp(meta, "pagetype", ""), // String
    prevsection: getProp(page, "lastSection", ""), // String
    sectionname: getProp(adops, "formattedSectionName", ""), // String
    url_path_1: getProp(adops, "pageTargets.pp0", ""), // String
    url_path_2: getProp(adops, "pageTargets.pp1", ""), // String
    url_path_3: getProp(adops, "pageTargets.pp2", ""), // String
    url_path_4: getProp(adops, "pageTargets.pp3", ""), // String
    url_path_5: getProp(adops, "pageTargets.pp4", ""), // String
    videoname: isVideoPage ? getProp(article, "title", "") : "", // String
    videosection: isVideoPage ? getProp(adops, "formattedSectionName", "") : "", // String
    videosource: isVideoPage ? getProp(article, "creditline", "") : "" // String
  };
}

/**
 * Set the related Permutive page targets for GPT Ad's
 */
export function permutiveSetTargets() {
  if (!permutiveEnabled || gptGetTarget("permutive").length > 0) {
    return;
  }
  try {
    const item = window.localStorage.getItem("_pdfps");
    const targets = item ? JSON.parse(item) : [];
    gptSetTarget("permutive", targets);
  } catch (e) {
    logger.error(`${debugTag} setting targets`, e);
  }
}

/**
 * Is the user a visitor from the EU or California? According to Akamai.
 * @param {Object} [list = {}] - exclude list
 * @param {String} [country = ""] - of user
 * @param {String} [region = ""] - of user
 * @returns {Boolean} - true when found
 */
export function isUserExcluded(list = {}, country = "", region = "") {
  const result = Object.keys(list).find(item => {
    // If optional region present then compare value of country and region(s).
    if (list[item].length) {
      return item === country && list[item].includes(region);
    }
    // Else only compare country value.
    return item === country;
  });
  return !!result;
}
